'use client';

import { useEffect, useRef, useState } from 'react';
import { Leap, Ripple } from 'loading-dev';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { BodyDefault } from '../../../components/typography';
import { useTranslations } from 'next-intl';
import { CHAT_STEP_BODY_FONT_SIZE_PX } from './chat-nesting-layout';
import { CHAT_TEXT_SHIMMER_STYLE } from './chat-text-shimmer';
import './generating-indicator.css';

const RECONNECTING_FADE_MS = 320;

export function useGeneratingIndicatorPresence({
  shouldShow,
  isReconnecting,
}: {
  shouldShow: boolean;
  isReconnecting: boolean;
}): {
  visible: boolean;
  showAsReconnecting: boolean;
  isExitingReconnecting: boolean;
} {
  const wasReconnectingRef = useRef(false);
  const [isExitingReconnecting, setIsExitingReconnecting] = useState(false);

  useEffect(() => {
    if (isReconnecting) {
      wasReconnectingRef.current = true;
      setIsExitingReconnecting(false);

      return;
    }

    if (shouldShow) {
      wasReconnectingRef.current = false;
      setIsExitingReconnecting(false);

      return;
    }

    if (!wasReconnectingRef.current) {
      return;
    }

    wasReconnectingRef.current = false;

    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setIsExitingReconnecting(false);

      return;
    }

    setIsExitingReconnecting(true);
    const timeoutId = window.setTimeout(() => {
      setIsExitingReconnecting(false);
    }, RECONNECTING_FADE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isReconnecting, shouldShow]);

  const showAsReconnecting = isReconnecting || isExitingReconnecting;

  return {
    visible: shouldShow || isExitingReconnecting,
    showAsReconnecting,
    isExitingReconnecting,
  };
}

export function GeneratingIndicator({
  isReconnecting = false,
  isExitingReconnecting = false,
  isCompacting = false,
  compactionSummaryText = null,
}: {
  isReconnecting?: boolean;
  isExitingReconnecting?: boolean;
  isCompacting?: boolean;
  compactionSummaryText?: string | null;
}) {
  const t = useTranslations('designSystem.chat');
  const showAsReconnecting = isReconnecting || isExitingReconnecting;
  const label = showAsReconnecting
    ? t('reconnecting')
    : isCompacting
      ? t('summarizing')
      : t('thinking');
  const streamedSummary =
    isCompacting && !showAsReconnecting && compactionSummaryText
      ? compactionSummaryText
      : null;
  const Spinner = showAsReconnecting ? Ripple : Leap;

  return (
    <VStack
      className={
        isExitingReconnecting
          ? 'chat-generating-indicator chat-generating-indicator--exit'
          : 'chat-generating-indicator'
      }
      w="full"
      gap={0.5}
      alignItems="stretch"
      py={1}
      borderRadius="sm"
      data-reconnecting={showAsReconnecting ? 'true' : 'false'}
      data-exiting={isExitingReconnecting ? 'true' : 'false'}
    >
      <HStack w="full" gap={1.5} alignItems="center">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          width={`${CHAT_STEP_BODY_FONT_SIZE_PX}px`}
          height={`${CHAT_STEP_BODY_FONT_SIZE_PX}px`}
          aria-hidden
        >
          <Spinner
            size={CHAT_STEP_BODY_FONT_SIZE_PX}
            color={token('colors.fg.muted')}
          />
        </Box>
        <BodyDefault
          as="span"
          fontSize={`${CHAT_STEP_BODY_FONT_SIZE_PX}px`}
          lineHeight="1.6"
          fontWeight="400"
          color="fg.muted"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          maxWidth="560px"
          animationStyle="shimmer"
          style={CHAT_TEXT_SHIMMER_STYLE}
        >
          {label}
        </BodyDefault>
        <Box flex={1} />
      </HStack>
      {streamedSummary && (
        <Box
          maxHeight="120px"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          borderLeftWidth="2px"
          borderLeftColor="border.subtle"
          pl={2.5}
        >
          <BodyDefault color="fg.subtle" whiteSpace="pre-wrap">
            {streamedSummary}
          </BodyDefault>
        </Box>
      )}
    </VStack>
  );
}
